import axios from 'axios';
import { Alert } from 'react-native';

import { API_URL } from '../config';
import { navigate } from './navigationService';

import { createLogger } from '../utils/logger';
import { redactError } from '../utils/redactError';
import { endSessionAndShowSignIn, isSessionOver } from './auth/sessionEndedFetchGuard';
import { getAuthToken } from './auth/tokenStorage';

const logger = createLogger('Http');
// Flag to prevent multiple upgrade alerts at once
let isShowingUpgradeAlert = false;

// Create axios instance with base URL
const http = axios.create({
  baseURL: API_URL,
  timeout: 15000, // 15 second timeout (increased for AI operations)
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
http.interceptors.request.use(
  async (config) => {
    const token = await getAuthToken();
    logger.debug('[HTTP Debug] Token present:', !!token);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      logger.warn('[HTTP Debug] No token available - request will be unauthorized');
    }
    logger.debug('[HTTP Debug] Request URL:', config.baseURL + config.url);
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
http.interceptors.response.use(
  (response) => {
    logger.debug(`[HTTP Debug] Success ${response.status}`);
    return response;
  },
  (error) => {
    logger.error('[HTTP Debug] Request failed', redactError(error));

    // A 401 from the auth guard means the session is over: the token is
    // missing, expired, signed out, or issued before the account's sessions
    // were ended (a password reset or change). It carries SIGN_IN_REQUIRED.
    // A route's own 401 (older servers: a wrong invitation answer) is left
    // to the screen that made the request. Wipe the session (tokens,
    // session-scoped storage, in-memory caches; @email_verified and
    // @2fa_setup_complete stay so the next launch lands on Login) and show
    // Sign In, so the person is not left on a screen of empty data. Plain
    // fetch calls get the same treatment from sessionEndedFetchGuard.
    if (error.response?.status === 401 && isSessionOver(error.response?.data)) {
      logger.error('[HTTP Debug] 401: the session is over');
      void endSessionAndShowSignIn();
    }

    // Handle TRIAL_EXPIRED error (403 Forbidden with specific code)
    if (error.response?.status === 403) {
      const responseData = error.response?.data;
      if (responseData?.code === 'TRIAL_EXPIRED' || responseData?.upgradeRequired) {
        logger.debug('[HTTP Debug] Trial expired - showing upgrade prompt');

        // Prevent multiple alerts
        if (!isShowingUpgradeAlert) {
          isShowingUpgradeAlert = true;

          Alert.alert(
            'Upgrade Required',
            responseData?.message || 'Your free trial has ended. Upgrade to continue adding and editing data.',
            [
              {
                text: 'View Plans',
                onPress: () => {
                  isShowingUpgradeAlert = false;
                  navigate('Upgrade' as any);
                },
              },
              {
                text: 'Later',
                style: 'cancel',
                onPress: () => {
                  isShowingUpgradeAlert = false;
                },
              },
            ],
            { cancelable: false }
          );
        }

        // Create a custom error that components can identify
        const trialExpiredError = new Error('TRIAL_EXPIRED');
        (trialExpiredError as any).isTrialExpired = true;
        (trialExpiredError as any).response = error.response;
        return Promise.reject(trialExpiredError);
      }
    }

    return Promise.reject(error);
  }
);

export default http;

// Helper function to check if error is due to trial expiration
export const isTrialExpiredError = (error: any): boolean => {
  return error?.isTrialExpired === true ||
    error?.response?.data?.code === 'TRIAL_EXPIRED' ||
    error?.response?.data?.upgradeRequired === true;
};